import { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Label } from '@progress/kendo-react-labels';
import { Button } from "@progress/kendo-react-buttons";
import { FieldWrapper } from "@progress/kendo-react-form";
import { fetchApi } from '../../services/api';
import { ILink } from "../../types/link";

type Props = {
  salespersonId: number;
  close: () => void;
  refresh: () => void;
}

type ApiResponse = {
  UserName: string;
  QuarterlyCommissionBudget: number | null;
  QuarterlyRevenueGoal: number | null;
  Links: ILink[];
}

const SalespersonPerformanceGoalDialog = ({ salespersonId, close, refresh }: Props) => {

  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ApiResponse>(null);

  // State for the form fields
  const [quarterlyCommissionBudget, setQuarterlyCommissionBudget] = useState<number>(null);
  const [quarterlyRevenueGoal, setQuarterlyRevenueGoal] = useState<number>(null);

  // Update internal data
  useEffect(() => {
    fetchApi(`/api/Customer/PerformanceGoal/${salespersonId}`)
      .then((data: ApiResponse) => {
        setData(data);
        setQuarterlyCommissionBudget(data.QuarterlyCommissionBudget);
        setQuarterlyRevenueGoal(data.QuarterlyRevenueGoal);
      });
  }, [salespersonId]);

  const update = () => {
    setSaving(true);
    const payload = {
      QuarterlyCommissionBudget: quarterlyCommissionBudget,
      QuarterlyRevenueGoal: quarterlyRevenueGoal,
    }
    const link = data.Links.find(x => x.Name == "Update");
    fetchApi(link.Link, payload, link.Method)
      .then(() => {
        refresh();
        close();
      })
      .catch((e) => {
        setSaving(false);
        // If not problem details
        if (!e?.status) alert('Unable to set goal.');
      });
  }

  const dataView = () => {
    return <form className="k-form k-form-md p-0">
      <FieldWrapper>
        <Label>Quarterly Commission Budget</Label>
        <NumericTextBox
          value={quarterlyCommissionBudget}
          min={1}
          format="c2"
          placeholder="Leave blank for no goal"
          onChange={(e) => setQuarterlyCommissionBudget(e.value)}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Label>Quarterly Revenue Goal</Label>
        <NumericTextBox
          value={quarterlyRevenueGoal}
          min={1}
          format="c2"
          placeholder="Leave blank for no goal"
          onChange={(e) => setQuarterlyRevenueGoal(e.value)}
        />
      </FieldWrapper>
    </form>
  }

  const loadingView = () => {
    return <div className="k-pos-absolute k-top-center mt-5">
      <Loader type="converging-spinner" />
    </div>;
  }

  return <Dialog
      title={`${data?.UserName}`} className="dialog-w525"
      onClose={close}
    >
      {data === null ? loadingView() : dataView()}
      <DialogActionsBar>
        <Button disabled={saving} onClick={close}>
          Cancel
        </Button>
        {data?.Links.find(x => x.Name == "Update") && <Button type="button" disabled={saving} themeColor="primary" onClick={update}>
          Update
        </Button>}
      </DialogActionsBar>
    </Dialog>
}

export default SalespersonPerformanceGoalDialog;